import { useCallback } from 'react';

import { PopupNames } from 'core';
import { IConfirmPopup } from 'molecules';

import { usePopupStateOperations } from '../Popups';

/**
 * Options for a single confirmation prompt.
 *
 * The hook is presentation-agnostic: callers resolve their own i18next strings
 * (via `useTranslation`) and pass the already-translated text in. This keeps
 * the confirmation flow free of any locale-key knowledge so the same hook works
 * for terminate (Students tab) and material delete (Materials tab) alike.
 */
export interface IGroupConfirmOptions {
  /** Localized dialog heading. */
  title: string;
  /** Localized body copy describing the consequence of confirming. */
  message: string;
  /** Localized confirm button label; falls back to the popup default. */
  confirmLabel?: string;
  /** Localized cancel button label; falls back to the popup default. */
  cancelLabel?: string;
}

/**
 * Thin wrapper over the global-modal `confirmPopup` convention
 * (`PopupNames.confirmPopup` → `molecules/ConfirmPopup`, rendered by
 * `GlobalModal`) that exposes a promise-style confirm.
 *
 * The returned function opens the shared confirmation modal and resolves:
 *   - `true`  when the operator confirms,
 *   - `false` when the operator cancels.
 *
 * It deliberately does **not** own the mutation or the refresh. Callers compose
 * the atomic confirm→act→refresh-on-success flow themselves, e.g.:
 *
 * ```ts
 * if (await confirm({ title, message })) {
 *   const ok = await terminate();
 *   if (ok) refetch();
 * }
 * ```
 *
 * Keeping act + refresh in the caller means the roster/list is refreshed only
 * on a successful payload and left unchanged on failure (Req 12.2), while a
 * cancel performs no mutation at all (Req 12.3). Confirmation copy is supplied
 * already translated through i18next by the caller (Req 12.4).
 */
export const useGroupConfirm = (): ((
  options: IGroupConfirmOptions,
) => Promise<boolean>) => {
  const {
    operations: { showPopup, hidePopup },
  } = usePopupStateOperations();

  return useCallback(
    ({ title, message, confirmLabel, cancelLabel }: IGroupConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        const settle = (result: boolean) => {
          hidePopup(PopupNames.confirmPopup);
          resolve(result);
        };

        showPopup<IConfirmPopup>(PopupNames.confirmPopup, {
          title,
          children: message,
          confirmText: confirmLabel,
          cancelText: cancelLabel,
          onConfirm: () => settle(true),
          onCancel: () => settle(false),
        });
      }),
    [showPopup, hidePopup],
  );
};
